import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import {
  listWorkspaces,
  createWorkspace,
  inviteToWorkspace,
  removeWorkspaceMember,
  deleteWorkspace,
} from "@/lib/api";
import { UsersThree, Plus, X, Trash, EnvelopeSimple, Crown } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

export default function Workspaces() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState({});

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  const load = async () => {
    setFetching(true);
    try {
      const list = await listWorkspaces();
      setItems(list);
    } catch {
      toast.error("Could not load workspaces");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createWorkspace(newName.trim());
      setNewName("");
      toast.success("Workspace created");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (workspaceId) => {
    const email = inviteEmail[workspaceId]?.trim();
    if (!email) return;
    try {
      await inviteToWorkspace(workspaceId, email);
      setInviteEmail((s) => ({ ...s, [workspaceId]: "" }));
      toast.success(`Invited ${email}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    }
  };

  const handleRemove = async (workspaceId, email) => {
    try {
      await removeWorkspaceMember(workspaceId, email);
      await load();
    } catch {
      toast.error("Remove failed");
    }
  };

  const handleDelete = async (workspaceId) => {
    if (!confirm("Delete this workspace? Offers will revert to private vaults.")) return;
    try {
      await deleteWorkspace(workspaceId);
      toast.success("Workspace deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" />

      <div className="flex-1">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-3">
              Workspaces
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-none">
              Forge with your <span className="font-black">team</span>.
            </h1>
            <p className="mt-4 text-[#9AA5B5] max-w-2xl">
              Create a shared vault and invite teammates by email. Everyone in
              a workspace sees the same offers.
            </p>
          </motion.div>

          {/* Create */}
          <form
            onSubmit={handleCreate}
            className="mt-10 bg-[#0F1F38] border border-white/5 rounded-lg p-6 md:p-8 flex flex-wrap items-end gap-4"
            data-testid="workspace-create-form"
          >
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
                Create new workspace
              </Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Acme Agency"
                className="mt-2 bg-[#0A1628] border-white/15 focus-visible:border-[#C9A961] focus-visible:ring-0 text-[#F5F1E8] rounded-sm"
                data-testid="workspace-name-input"
              />
            </div>
            <Button
              type="submit"
              disabled={!newName.trim() || creating}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm"
              data-testid="workspace-create-btn"
            >
              <Plus weight="bold" className="mr-2 h-4 w-4" />
              {creating ? "Creating…" : "Create"}
            </Button>
          </form>

          {/* List */}
          <div className="mt-10 space-y-4">
            {fetching ? (
              <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-10 text-center text-[#9AA5B5]">
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div
                className="bg-[#0F1F38] border border-white/5 rounded-lg p-10 text-center"
                data-testid="workspace-empty-state"
              >
                <UsersThree
                  weight="duotone"
                  className="h-10 w-10 text-[#C9A961] mx-auto mb-4"
                />
                <p className="text-sm text-[#9AA5B5]">
                  You don&apos;t have any workspaces yet.
                </p>
              </div>
            ) : (
              items.map((ws) => {
                const isOwner = ws.owner_user_id === user.user_id;
                return (
                  <div
                    key={ws.workspace_id}
                    className="bg-[#0F1F38] border border-white/5 rounded-lg p-6 md:p-8"
                    data-testid={`workspace-card-${ws.workspace_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-display font-bold text-xl text-white">
                            {ws.name}
                          </h3>
                          {isOwner && (
                            <Crown
                              weight="fill"
                              className="h-4 w-4 text-[#C9A961]"
                              title="Owner"
                            />
                          )}
                        </div>
                        <p className="text-xs text-[#9AA5B5] mt-1">
                          Owned by {ws.owner_email}
                        </p>
                      </div>
                      {isOwner && (
                        <button
                          onClick={() => handleDelete(ws.workspace_id)}
                          className="text-[#808A9B] hover:text-[#ef4444] transition-colors p-1"
                          data-testid={`workspace-delete-${ws.workspace_id}`}
                          title="Delete workspace"
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Members */}
                    <div className="space-y-2 mb-4">
                      {(ws.member_emails || []).length === 0 && (
                        <p className="text-xs text-[#808A9B]">No members yet.</p>
                      )}
                      {(ws.member_emails || []).map((email) => (
                        <div
                          key={email}
                          className="flex items-center justify-between bg-[#0A1628] border border-white/5 rounded-sm px-3 py-2 text-sm"
                          data-testid={`workspace-member-${email}`}
                        >
                          <span className="text-[#F5F1E8]">{email}</span>
                          {isOwner && (
                            <button
                              onClick={() => handleRemove(ws.workspace_id, email)}
                              className="text-[#808A9B] hover:text-[#ef4444]"
                              aria-label="Remove member"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Invite */}
                    {isOwner && (
                      <div className="flex gap-2 flex-wrap">
                        <Input
                          type="email"
                          value={inviteEmail[ws.workspace_id] || ""}
                          onChange={(e) =>
                            setInviteEmail((s) => ({
                              ...s,
                              [ws.workspace_id]: e.target.value,
                            }))
                          }
                          placeholder="teammate@example.com"
                          className="flex-1 min-w-[200px] bg-[#0A1628] border-white/15 focus-visible:border-[#C9A961] focus-visible:ring-0 text-[#F5F1E8] rounded-sm"
                          data-testid={`workspace-invite-input-${ws.workspace_id}`}
                        />
                        <Button
                          onClick={() => handleInvite(ws.workspace_id)}
                          className="bg-white/5 text-white hover:bg-white/10 border border-white/10 rounded-sm"
                          data-testid={`workspace-invite-btn-${ws.workspace_id}`}
                        >
                          <EnvelopeSimple weight="bold" className="mr-2 h-4 w-4" />
                          Invite
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
